/**
 * The pocket, along the bottom of the screen: three slots, numbered, showing what each
 * one holds.
 *
 * The numbers are not decoration — they are what the desktop bindings (`1`, `2`, `3`)
 * refer to, so a player told to press 2 can see which slot that is. And the slots are
 * real buttons rather than a read-out, because on touch the tap *is* the retrieval: the
 * display and the control are the same thing (design D7).
 *
 * Like every other producer, a press here writes to `InputState` and nothing more.
 * `PocketController` decides on the next frame whether anything comes out — the HUD does
 * not know where the camera is, and taking an item out is a question about the world.
 */

import { useEffect } from 'react';
import { OBJECT_KINDS } from '../game/objectKinds';
import { hasPocket } from '../game/pocket';
import { inputState } from '../input/inputState';
import { useGameStore } from '../store/gameStore';

/** How long a refusal stays up. Long enough to read, short enough not to become HUD. */
const MESSAGE_MS = 2500;

export function PocketHud() {
  const pocket = useGameStore((store) => store.pocket);
  const itemMessage = useGameStore((store) => store.itemMessage);
  const pocketed = useGameStore((store) => hasPocket(store.profile));
  const clearItemMessage = useGameStore((store) => store.clearItemMessage);

  useEffect(() => {
    if (itemMessage === null) return;
    const timer = window.setTimeout(clearItemMessage, MESSAGE_MS);
    return () => window.clearTimeout(timer);
  }, [itemMessage, clearItemMessage]);

  return (
    // Unnamed where there is no pocket, so it stops being a landmark: a region a screen
    // reader announces as "Pocket" in a game that has none is worse than no region. The
    // message inside is its own live region and announces itself either way.
    <section className="pocket-hud" aria-label={pocketed ? 'Pocket' : undefined}>
      {itemMessage !== null && (
        <p className="pocket-hud__message" role="status" aria-live="polite">
          {itemMessage}
        </p>
      )}
      {/* The slots go where the game has no pocket (`per-game-pocket` D5); the message
          above them does not. That line is not the pocket's — the dog assembly writes to
          it too — and a game with nothing to stow still has things to say. */}
      {pocketed && (
        <div className="pocket-hud__slots">
          {pocket.map((item, slot) => (
            <button
              key={slot}
              type="button"
              data-hud
              className={item ? 'pocket-hud__slot pocket-hud__slot--full' : 'pocket-hud__slot'}
              // The label carries both halves: a slot named only by its contents cannot be
              // aimed at by number, and one named only by number says nothing to a screen
              // reader about what is in it.
              aria-label={`Slot ${slot + 1}: ${item ? OBJECT_KINDS[item.kind].label : 'empty'}`}
              // `onClick` rather than `onPointerDown`, as the pause button does: retrieval is
              // deliberate, a graze that slides off should not empty a slot, and a click
              // brings Enter and Space with it for free.
              onClick={() => {
                inputState.pocketSlot = slot;
              }}
            >
              <span className="pocket-hud__number" aria-hidden>
                {slot + 1}
              </span>
              <span className="pocket-hud__label" aria-hidden>
                {item ? OBJECT_KINDS[item.kind].label : 'Empty'}
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
